import { Component, HelpCircle } from "lucide-react";
import { ContextualHelp } from "@shared/ui/contextual-help";
import { SelfServiceDashboard } from "./components/SelfServiceDashboard";

import { TipsSettings } from "./components/TipsSettings";
import { ServiceCenterSettings } from "./components/ServiceCenterSettings";
import { Tabs } from "@shared/ui/tabs";
import { FAQSettings } from "./components/FAQSettings";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";
import { useRemoteUtilityId, useRemoteUtilityName, useUserName, useUtilityAlias } from "@shared/selectors/globalSelectors";
import { BrandingLanding } from "./components/branding/Landing";
import Widgets from "./components/widgets/Widgets";

const SelfServicePage = () => {
  const tabComponents = {
    dashboard: {
      label: "Dashboard",
      component: <SelfServiceDashboard />,
      className:
        "data-[state=active]:bg-white data-[state=active]:text-foreground",
    },
    branding: {
      label: "Branding",
      component: <BrandingLanding />
    },
    widgets: {
      label: "Widgets",
      component: <Widgets />
    },
    faqs: {
      label: "FAQs",
      component: <FAQSettings />,
      className:
        "data-[state=active]:bg-white data-[state=active]:text-foreground",
    },
    tips: {
      label: "Tips",
      shortLabel: "Tips",
      component: <TipsSettings />,
      className:
        "data-[state=active]:bg-white data-[state=active]:text-foreground",
    },
    "service-centers": {
      label: "Service Centers",
      shortLabel: "Centers",
      component: <ServiceCenterSettings />,
      className:
        "data-[state=active]:bg-white data-[state=active]:text-foreground",
    },
  
  };

  const urlMapping = {
    dashboard: "dashboard",
    faqs: "faqs",
    tips: "tips",
    "service-centers": "service-centers",
    "branding":"branding",
    "widgets":"widgets"
  };
  // const baseUrl = import.meta.env.VITE_CX_WEB_BASE_URL;
  // const handleClick = () => {
  //   window.location.href = `${baseUrl}/login/samoa_water_authority`;
  // };
  const baseUrl = import.meta.env.VITE_CX_WEB_BASE_URL; // example: https://consumer-new.bynry.com
  const userProfileData = useGlobalUserProfile();
  const tenantAlias = userProfileData?.data?.utility?.tenant?.alias;
  const utilityAlias = useUtilityAlias();

  const selfServiceUrl = tenantAlias
    ? `${baseUrl}/login/${tenantAlias}/${utilityAlias || ''}`
    : baseUrl; // fallback if alias missing
    
  return (
    <div className="">
      <div className="flex items-center">
        <h1 className="page-title">Self Service</h1>
        <ContextualHelp
          title="Self Service Configuration"
          content="Configure FAQs, tips, and service centers to help users find answers to common questions and provide helpful guidance throughout the application."
        />
      </div>

      <p className="page-subtitle pb-4">
        Manage frequently asked questions, helpful tips, and service center
        information to improve user experience and reduce support requests.
      </p>
      <div className="text-gray-800 mb-4">
        Self Service Portal:{" "}
        <a
          href={selfServiceUrl}
          target="_blank" // opens in new tab
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {selfServiceUrl}
        </a>
      </div>

      <Tabs
        defaultValue="dashboard"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        tabsListClassName="grid w-full grid-cols-6 mb-6"
        tabsListFullWidth={true}
        idPrefix="self-service-tab"
        className="w-full"
      />

      {/* Help tooltips for each tab - positioned absolutely or as overlay */}
      <div className="hidden">
        <ContextualHelp
          title="Performance Dashboard"
          content="View analytics and performance metrics for your self-service portal, including FAQ and tip effectiveness."
        />
        <ContextualHelp
          title="FAQ Management"
          content="Create and manage frequently asked questions to help users find quick answers to common queries."
        />
        <ContextualHelp
          title="Tips Configuration"
          content="Set up helpful tips and guidance that appear throughout the application to assist users."
        />
        <ContextualHelp
          title="Service Centers"
          content="Configure service center locations, contact details, and operating hours for user reference."
        />
      </div>
    </div>
  );
};

export default SelfServicePage;
